/**
 * Turning a chord line + lyric line, or a line with inline chords, into slots.
 */
import { charWidth } from './columns.js';
import { isMarker, isUnambiguousChord, readChord, toChartToken } from './chordTokens.js';
const clean = (text) => {
    const trimmed = text.trim();
    return trimmed === '' ? null : trimmed;
};
// Letters, combining marks and apostrophes ("they're"), but not CJK: a wide
// character is a syllable, and a chord over one is exact.
const isWordLetter = (ch) => ch !== undefined && charWidth(ch) < 2 && /[\p{L}\p{M}'’]/u.test(ch);
/**
 * Chords are typed over the first letter of a word, and are often off by one
 * column. Ported from song-fetcher (transforms/word_normalize.py):
 * - on the 2nd letter of a word → move to the word's first letter
 * - on the last letter of a word → move to the space after it
 */
const nudgeToWordBoundary = (chars, i) => {
    if (isWordLetter(chars[i]) && isWordLetter(chars[i - 1]) && !isWordLetter(chars[i - 2]))
        return i - 1;
    if (isWordLetter(chars[i]) && isWordLetter(chars[i - 1]) && !isWordLetter(chars[i + 1]))
        return i + 1;
    return i;
};
/**
 * Cuts a lyric line at the given chord columns. Text before the first chord
 * (a pickup) is returned separately; each chord gets the text from its
 * column up to the next chord.
 */
export const splitLyricsAtColumns = (lyrics, columns) => {
    const chars = Array.from(lyrics);
    const ends = [];
    let column = 0;
    for (const ch of chars) {
        column += charWidth(ch);
        ends.push(column);
    }
    // The character under a column; a column past the end maps to the end.
    const indexAt = (col) => {
        const i = ends.findIndex(end => end > col);
        return i === -1 ? chars.length : i;
    };
    // Chord tokens are at least two columns apart, and neighbouring nudges can't
    // meet: moving right needs a non-letter after the word, moving left a letter.
    const cuts = columns.map(col => nudgeToWordBoundary(chars, indexAt(col)));
    const piece = (from, to) => clean(chars.slice(from, to).join(''));
    return {
        pickup: cuts.length > 0 ? piece(0, cuts[0]) : clean(lyrics),
        fragments: cuts.map((cut, k) => piece(cut, k + 1 < cuts.length ? cuts[k + 1] : chars.length)),
    };
};
/** Slots for a chord line, with the lyric line under it (if any) cut at the chord columns. */
export const slotsFromChordLine = (tokens, lyrics) => {
    if (lyrics === null)
        return tokens.map(token => ({ token: toChartToken(token), lyrics: null }));
    const { pickup, fragments } = splitLyricsAtColumns(lyrics, tokens.map(token => token.column));
    const slots = tokens.map((token, i) => ({ token: toChartToken(token), lyrics: fragments[i] }));
    return pickup ? [{ token: null, lyrics: pickup }, ...slots] : slots;
};
const inlineToken = (inner) => {
    const text = inner.trim().replace(/^\*/, ''); // ChordPro annotation: [*Riff]
    const chord = readChord(text);
    return chord ? { kind: 'chord', chord } : { kind: 'custom', text };
};
const BRACKET = /\[([^\]]*)\]/g;
const PAREN = /\(([^()]*)\)/g;
const parenChordMatches = (line) => [...line.matchAll(PAREN)].filter(match => isUnambiguousChord(match[1].trim()));
/** "[G]Hello", "[N.C.]", "[*Riff]" — a ChordPro inline chord or annotation anywhere in the line. */
export const hasInlineChords = (line) => [...line.matchAll(BRACKET)].some(match => {
    const inner = match[1].trim();
    return readChord(inner) !== null || isMarker(inner) || /^\*\S/.test(inner);
});
/**
 * "(G)Hello (C)darkness" — chords in parentheses used as inline chords.
 * Needs two of them, or one glued to the word it falls on; a lone "(C)" is
 * more likely a copyright sign, and "(oh)" stays lyrics either way.
 */
export const hasInlineParenChords = (line) => {
    const matches = parenChordMatches(line);
    return (matches.length >= 2 ||
        matches.some(match => /\S/.test(line.charAt((match.index ?? 0) + match[0].length))));
};
/**
 * Slots for a line with inline chords: "[G]Hello [C]dar[D]kness". Each chord
 * takes the text up to the next chord; text before the first one gets a
 * slot of its own (repeating the chord sounding there, once materialized).
 */
export const slotsFromInlineChords = (line, style) => {
    const markers = style === 'bracket'
        ? [...line.matchAll(BRACKET)].filter(match => match[1].trim() !== '')
        : parenChordMatches(line);
    const slots = [];
    const pickup = clean(line.slice(0, markers[0]?.index ?? line.length));
    if (pickup)
        slots.push({ token: null, lyrics: pickup });
    markers.forEach((match, i) => {
        const start = (match.index ?? 0) + match[0].length;
        const end = i + 1 < markers.length ? markers[i + 1].index : line.length;
        slots.push({ token: inlineToken(match[1]), lyrics: clean(line.slice(start, end)) });
    });
    return slots;
};
/** Slots for "G  C   Hello darkness": every chord, with the words under the last one. */
export const slotsFromLeadingChords = (tokens, lyrics) => tokens.map((token, i) => ({
    token: toChartToken(token),
    lyrics: i === tokens.length - 1 ? clean(lyrics) : null,
}));
/** Slot for a lyric line with no chords over it. */
export const slotsFromLyrics = (lyrics) => [{ token: null, lyrics: clean(lyrics) }];
const isHighSurrogate = (code) => code >= 0xd800 && code <= 0xdbff;
/**
 * Splits text longer than `max` into chunks at word boundaries (a single
 * word longer than `max` is cut, never inside a surrogate pair). Each chunk
 * fits a slot's lyrics field.
 */
export const wrapLyrics = (text, max) => {
    const chunks = [];
    let rest = text;
    while (rest.length > max) {
        let cut = rest.lastIndexOf(' ', max);
        if (cut <= 0) {
            cut = max;
            if (isHighSurrogate(rest.charCodeAt(cut - 1)))
                cut = cut > 1 ? cut - 1 : cut + 1;
        }
        chunks.push(rest.slice(0, cut).trimEnd());
        rest = rest.slice(cut).trimStart();
    }
    if (rest)
        chunks.push(rest);
    return chunks;
};
